import { Alert, Box, Stack, Typography } from '@mui/material';
import {
  deleteWebmeticsMapping,
  getAllWebmeticsMappings,
  saveWebmeticsMapping,
  syncWebmeticsMapping,
  testWebmeticsMapping,
  updateWebmeticsMapping,
} from 'API/webmetics';
import { ProductGroups, ZoomLevels } from 'enums';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import PaiButton from 'features/ui/base-components/pai-button';
import { useUserData } from 'hooks/use-user-data';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import { ApiResponse, StatusMessage, WebmeticsMapping } from './webmetics.types';
import ConfirmDeleteMappingsDialog from './confirm-delete-mappings-dialog';
import CreateWebmeticsMappingDialog from './create-webmetics-mapping-dialog';
import MappingTable from './mappingtable';
import TestMappingResults from './test-mapping-results';

const WebmeticsIntegrations: React.FC = () => {
  const { classes } = useStyles();
  const { token, tenant } = useUserData();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [mappings, setMappings] = useState<WebmeticsMapping[]>([]);
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [isLoadingMappings, setIsLoadingMappings] = useState<boolean>(false);

  const [deleteMappingDialog, setDeleteMappingDialog] = useState<{ open: boolean; loading: boolean; mappingId: string | null }>({
    open: false,
    loading: false,
    mappingId: null,
  });
  const [createMappingDialog, setCreateMappingDialog] = useState<{ open: boolean; loading: boolean; currentMapping: WebmeticsMapping | null }>({
    open: false,
    loading: false,
    currentMapping: null,
  });

  const [syncMappingLoading, setSyncMappingLoading] = useState<boolean>(false);
  const [testMapping, setTestMapping] = useState<{ result: any; open: boolean; loading: boolean; errorMessage: string | null }>({
    result: null,
    open: false,
    loading: false,
    errorMessage: null,
  });

  /**
   * Fetch Mappings
   */

  const fetchMappings = async () => {
    setIsLoadingMappings(true);
    try {
      const response = await getAllWebmeticsMappings();
      if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch Webmetics mappings.');
      }
      setMappings(response.data || []);
    } catch (error: any) {
      console.error('Error fetching Webmetics mappings:', error);
      setStatusMessage({
        message: error.message || 'Error fetching mappings.',
        type: 'error',
      });
    } finally {
      setIsLoadingMappings(false);
    }
  };

  useEffect(() => {
    if (tenant && token) {
      fetchMappings();
    }
  }, [tenant, token]);

  const getQueryParams = () => new URLSearchParams(location.search);
  useEffect(() => {
    const queryParams = getQueryParams();
    const status = queryParams.get('status');
    if (status) {
      let message = '';
      let type: 'error' | 'success' | 'info' | 'warning' = 'info';
      switch (status) {
        case 'synced':
          message = 'Mapping synced successfully.';
          type = 'success';
          break;
        case 'failed':
          message = 'Failed to sync mapping.';
          type = 'error';
          break;
        default:
          break;
      }
      if (message) setStatusMessage({ message, type });
      queryParams.delete('status');
      navigate({ pathname: location.pathname, search: queryParams.toString() ? `?${queryParams.toString()}` : '' }, { replace: true });
    }
  }, [location.search, navigate]);

  /**
   * Sync Mapping handlers
   */

  const handleSyncMapping = async (mappingId: string) => {
    try {
      setSyncMappingLoading(true);
      const response = await syncWebmeticsMapping(mappingId);
      if (response.error) {
        throw new Error(response.error.message || 'Failed to sync mapping.');
      }
      setStatusMessage({
        message: 'Mapping synced successfully and search created.',
        type: 'success',
      });
      fetchMappings();
    } catch (error: any) {
      console.error('Error syncing mapping:', error);
      setStatusMessage({
        message: error.message || 'Error syncing mapping.',
        type: 'error',
      });
    } finally {
      setSyncMappingLoading(false);
    }
  };

  /**
   * Test Mapping Dialog handlers
   */

  const handleCloseTestMappingDialog = () => {
    setTestMapping((prev) => ({ ...prev, open: false }));
  };

  const handleCloseErrorMessage = () => {
    setTestMapping((prev) => ({ ...prev, errorMessage: null }));
  };

  const handleTestMapping = async (mappingId: string) => {
    setTestMapping((prev) => ({ ...prev, loading: true }));
    try {
      const response: ApiResponse<any> = await testWebmeticsMapping(mappingId);
      if (response.error) {
        throw new Error(response.error.message || 'Failed to test mapping.');
      }
      setTestMapping((prev) => ({ ...prev, result: response.data, open: true }));
    } catch (error: any) {
      console.error('Error testing mapping:', error);
      setTestMapping((prev) => ({ ...prev, errorMessage: error.message || 'Error testing mapping.' }));
    } finally {
      setTestMapping((prev) => ({ ...prev, loading: false }));
    }
  };

  /**
   * Create Mapping Dialog handlers
   */

  const openCreateMappingDialog = () => {
    setCreateMappingDialog((prev) => ({
      ...prev,
      open: true,
      currentMapping: null,
    }));
  };

  const openEditMappingDialog = (mapping: WebmeticsMapping) => {
    return () => {
      setCreateMappingDialog((prev) => ({
        ...prev,
        open: true,
        currentMapping: mapping,
      }));
    };
  };

  const handleCloseCreateMappingDialog = () => {
    setCreateMappingDialog({
      open: false,
      loading: false,
      currentMapping: null,
    });
  };

  const handleMappingSubmit = async (mappingData: Partial<WebmeticsMapping>) => {
    try {
      const { currentMapping } = createMappingDialog;
      setCreateMappingDialog((prev) => ({ ...prev, loading: true }));
      let response: ApiResponse<WebmeticsMapping>;
      if (currentMapping && currentMapping._id) {
        // For updates, _id is passed in the URL, not the payload
        response = await updateWebmeticsMapping(currentMapping._id, mappingData);
      } else {
        response = await saveWebmeticsMapping(mappingData);
      }
      if (response.error) {
        throw new Error(response.error.message || 'Failed to save mapping.');
      }
      setStatusMessage({
        message: currentMapping && currentMapping._id ? 'Mapping updated successfully.' : 'Mapping created successfully.',
        type: 'success',
      });
      fetchMappings();
      handleCloseCreateMappingDialog();
    } catch (error: any) {
      setCreateMappingDialog((prev) => ({ ...prev, loading: false }));
      console.error('Error saving mapping:', error);
      setStatusMessage({
        message: error.message || 'Error saving mapping.',
        type: 'error',
      });
    }
  };

  /**
   * Delete Mapping Dialog handlers
   */

  const handleOpenDeleteDialog = (mappingId: string) => {
    return () => {
      setDeleteMappingDialog((prev) => ({
        ...prev,
        open: true,
        mappingId,
      }));
    };
  };

  const handleCloseDeleteDialog = () => {
    setDeleteMappingDialog({ open: false, loading: false, mappingId: null });
  };

  const handleDeleteMapping = async () => {
    const { mappingId } = deleteMappingDialog;
    if (!mappingId) return;
    setDeleteMappingDialog((prev) => ({
      ...prev,
      loading: true,
    }));
    try {
      const response: ApiResponse<null> = await deleteWebmeticsMapping(mappingId);
      if (response.error) {
        throw new Error(response.error.message || 'Failed to delete mapping.');
      }
      setStatusMessage({
        message: 'Mapping deleted successfully.',
        type: 'success',
      });
      fetchMappings();
    } catch (error: any) {
      console.error('Error deleting mapping:', error);
      setStatusMessage({
        message: error.message || 'Error deleting mapping.',
        type: 'error',
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  return (
    <div className={classes.container}>
      <Box className={classes.header}>
        <Stack direction={'row'} gap={2} alignItems={'center'} justifyContent={'space-between'}>
          <Stack direction={'column'} gap={1}>
            <Typography className={classes.title}>{t('webmeticsIntegrationsTitle')}</Typography>
            <Typography className={classes.description}>{t('webmeticsIntegrationsDescription')}</Typography>
          </Stack>
          <Stack direction={'row'} gap={2} alignItems={'center'}>
            <PaiButton text={t('addMappingSet')} border={'rounded'} size={'small'} action={openCreateMappingDialog} />
          </Stack>
        </Stack>
      </Box>

      <Box className={classes.content}>
        {statusMessage && (
          <Alert className={classes.alert} severity={statusMessage.type} onClose={() => setStatusMessage(null)}>
            {statusMessage.message}
          </Alert>
        )}

        {isLoadingMappings ? (
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'} minHeight={'200px'}>
            <PaiSpinningLoader size={'medium'} />
          </Box>
        ) : (
          <MappingTable
            mappings={mappings}
            testMapping={testMapping}
            syncMappingLoading={syncMappingLoading}
            handleTestMapping={handleTestMapping}
            handleCloseErrorMessage={handleCloseErrorMessage}
            handleSyncMapping={handleSyncMapping}
            openEditMappingDialog={openEditMappingDialog}
            handleOpenDeleteDialog={handleOpenDeleteDialog}
          />
        )}
      </Box>

      <ConfirmDeleteMappingsDialog
        open={deleteMappingDialog.open}
        loading={deleteMappingDialog.loading}
        handleClose={handleCloseDeleteDialog}
        handleDeleteMapping={handleDeleteMapping}
      />
      <CreateWebmeticsMappingDialog
        open={createMappingDialog.open}
        handleClose={handleCloseCreateMappingDialog}
        onSubmit={handleMappingSubmit}
        initialMapping={createMappingDialog.currentMapping}
        productGroup={ProductGroups.LEAD_BOOSTER}
      />
      <TestMappingResults open={testMapping.open} onClose={handleCloseTestMappingDialog} testResult={testMapping.result} />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  header: {
    padding: '16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
  },
  description: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
  content: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  alert: {
    marginBottom: '16px',
    borderRadius: '8px',
  },
}));

export default WebmeticsIntegrations;
